import { Component, Inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { take } from 'rxjs';
import { IFieldConfig } from 'src/app/domain/IFieldConfig';
import { TypeCesEnum } from 'src/app/domain/registration/enums/type-ces.enum';
import { TypeComponentFieldEnum } from 'src/app/domain/registration/enums/type-component-dynamic.enum';
import { CustomerService } from '../crud-service';
import {
  CustomerBranch,
  defaultFiscalParameter,
  isMatrizBranch,
  MATRIZ_BRANCH_TITLE,
} from '../customer-branch.util';

export interface CustomerBranchDialogData {
  branch: CustomerBranch | null;
  index: number;
  viewMode: boolean;
}

@Component({
  selector: 'app-customer-branch-dialog',
  templateUrl: './customer-branch-dialog.component.html',
  styleUrls: ['./customer-branch-dialog.component.sass'],
})
export class CustomerBranchDialogComponent {
  readonly isMatriz: boolean;
  readonly viewMode: boolean;
  readonly fields: IFieldConfig[] = [
    { name: 'branchTitle', label: 'TÍTULO', type: TypeComponentFieldEnum.INPUT },
    { name: 'branchDocument', label: 'DOCUMENTO', type: TypeComponentFieldEnum.INPUT },
    { name: 'branchStateRegistration', label: 'INSCRIÇÃO ESTADUAL', type: TypeComponentFieldEnum.INPUT },
    { name: 'branchCep', label: 'CEP', type: TypeComponentFieldEnum.INPUT_CEP },
    { name: 'branchAddress', label: 'ENDEREÇO', type: TypeComponentFieldEnum.INPUT },
    { name: 'branchAddressNumber', label: 'NÚMERO', type: TypeComponentFieldEnum.INPUT },
    { name: 'branchAddressComplement', label: 'COMPLEMENTO', type: TypeComponentFieldEnum.INPUT },
    { name: 'branchNeighborhood', label: 'BAIRRO', type: TypeComponentFieldEnum.INPUT },
    { name: 'branchCity', label: 'CIDADE', type: TypeComponentFieldEnum.INPUT },
    { name: 'branchState', label: 'UF', type: TypeComponentFieldEnum.INPUT },
    { name: 'branchObservation', label: 'OBSERVAÇÃO', type: TypeComponentFieldEnum.INPUT },
  ];

  form = this.fb.group({
    branchTitle: ['', Validators.required],
    branchDocument: [''],
    branchStateRegistration: [''],
    branchCep: [''],
    branchAddress: [''],
    branchAddressNumber: [''],
    branchAddressComplement: [''],
    branchNeighborhood: [''],
    branchState: [''],
    branchCity: [''],
    branchCountry: ['BR'],
    branchObservation: [''],
    branchActive: [true],
  });

  constructor(
    private fb: FormBuilder,
    private customerService: CustomerService,
    private dialogRef: MatDialogRef<CustomerBranchDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CustomerBranchDialogData
  ) {
    this.viewMode = !!data?.viewMode;
    this.isMatriz = isMatrizBranch(data?.branch) || data?.index === 0;
    const branch = data?.branch;
    this.form.patchValue({
      branchTitle: this.isMatriz ? MATRIZ_BRANCH_TITLE : branch?.branchTitle || '',
      branchDocument: branch?.branchDocument || '',
      branchStateRegistration: branch?.branchStateRegistration || '',
      branchCep: branch?.branchCep || '',
      branchAddress: branch?.branchAddress || '',
      branchAddressNumber: branch?.branchAddressNumber || '',
      branchAddressComplement: branch?.branchAddressComplement || '',
      branchNeighborhood: branch?.branchNeighborhood || '',
      branchState: branch?.branchState || '',
      branchCity: branch?.branchCity || '',
      branchCountry: branch?.branchCountry || 'BR',
      branchObservation: branch?.branchObservation || '',
      branchActive: branch?.branchActive !== false,
    });
    if (this.viewMode) {
      this.form.disable({ emitEvent: false });
    } else if (this.isMatriz) {
      this.form.get('branchTitle')?.disable({ emitEvent: false });
      ['branchCep', 'branchAddress', 'branchAddressNumber', 'branchAddressComplement', 'branchNeighborhood', 'branchState', 'branchCity']
        .forEach((field) => this.form.get(field)?.disable({ emitEvent: false }));
    }
  }

  get fieldType(): TypeCesEnum {
    return this.viewMode ? TypeCesEnum.SHOW : TypeCesEnum.EDIT;
  }

  config(name: string): IFieldConfig {
    return this.fields.find((field) => field.name === name) || this.fields[0];
  }

  get title(): string {
    if (this.viewMode) return 'Visualizar filial';
    return this.data?.branch ? 'Editar filial' : 'Cadastrar filial';
  }

  lookupCep(): void {
    const cep = String(this.form.get('branchCep')?.value || '').replace(/\D/g, '');
    if (cep.length !== 8 || this.isMatriz || this.viewMode) return;
    this.customerService
      .getCepInfo(cep)
      .pipe(take(1))
      .subscribe({
        next: (result: any) => {
          this.form.patchValue({
            branchAddress: result?.address || this.form.get('branchAddress')?.value,
            branchNeighborhood: result?.neighborhood || this.form.get('branchNeighborhood')?.value,
            branchCity: result?.city || this.form.get('branchCity')?.value,
            branchState: result?.state || this.form.get('branchState')?.value,
          });
        },
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.getRawValue();
    const current = this.data?.branch;
    const branch: CustomerBranch = {
      localKey: current?.localKey || `branch-${Date.now()}`,
      id: current?.id ?? null,
      branchTitle: this.isMatriz ? MATRIZ_BRANCH_TITLE : String(value.branchTitle || ''),
      branchDocument: String(value.branchDocument || ''),
      branchStateRegistration: String(value.branchStateRegistration || ''),
      branchCep: String(value.branchCep || ''),
      branchAddress: String(value.branchAddress || ''),
      branchAddressNumber: String(value.branchAddressNumber || ''),
      branchAddressComplement: String(value.branchAddressComplement || ''),
      branchNeighborhood: String(value.branchNeighborhood || ''),
      branchState: String(value.branchState || ''),
      branchCity: String(value.branchCity || ''),
      branchCountry: String(value.branchCountry || 'BR'),
      branchObservation: String(value.branchObservation || ''),
      branchIsMain: this.isMatriz,
      branchActive: this.isMatriz ? true : value.branchActive !== false,
      fiscalParameter: defaultFiscalParameter(current?.fiscalParameter),
    };
    this.dialogRef.close({ branch, index: this.data?.index ?? -1 });
  }
}
